import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { WebSourcesGlobalService } from '@core/services/store/web-sources-global.service';
import {
    LivechatPrefsUI,
    LivechatSettings,
} from '@shared/features/livechat/types/PreferencesOptions';
import { ToastService } from '@shared/services/toast.service';
import { finalize } from 'rxjs';
import { WebSourceHttpService } from './web-source.http.service';

/**
 * The LIVECHAT of one of the account's websites (`web_source`): the account no longer has
 * a livechat of its own. The section lists the websites; with none, it registers one
 * (POST) and then saves its livechat (PUT …/livechat) and the agent that attends it
 * (PUT …/ai_agent).
 */
@Injectable()
export class LivechatPrefsService {
    private readonly toastS = inject(ToastService);
    private readonly httpS = inject(WebSourceHttpService);
    private readonly webSourcesS = inject(WebSourcesGlobalService);

    readonly isLoading = signal(false);
    readonly isCreating = signal(false);
    readonly isSavingAgent = signal(false);
    readonly state = this.webSourcesS.state;
    readonly webSources = this.webSourcesS.webSources;
    private readonly selectedId = signal<string | null>(null);

    readonly webSource = computed(() => {
        const list = this.webSources();
        return list.find(w => w.id === this.selectedId()) ?? list[0] ?? null;
    });
    readonly settings = computed<LivechatSettings | undefined>(
        () => this.webSource()?.livechat_settings ?? undefined
    );
    readonly livechatPrefs = computed(() => this.settings()?.preferences);

    static removeCredentials() {
        const ANONYMOUS_ID_KEY = 'wc_anonymous_id';
        const AUTH_TOKEN_KEY = 'wc_auth_token';
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(ANONYMOUS_ID_KEY);
    }

    /** Read the websites again on entering the section (they may change in TwinnyX). */
    getSettings() {
        if (!this.webSourcesS.isLoading()) this.webSourcesS.load();
    }

    select(id: string) {
        this.selectedId.set(id);
    }

    /** The first website of the account: registered without crawling, its livechat off. */
    createWebSource(url: string) {
        this.isCreating.set(true);
        this.httpS
            .create$({ url })
            .pipe(finalize(() => this.isCreating.set(false)))
            .subscribe({
                next: web => {
                    this.webSourcesS.upsert(web);
                    this.select(web.id);
                },
                error: (error: HttpErrorResponse) => {
                    const apiMessage =
                        typeof error.error?.message === 'string' ? error.error.message : undefined;
                    this.toastS.error('Failed to create the website', undefined, apiMessage);
                    console.error(error);
                },
            });
    }

    updatePreferences(preferences: LivechatPrefsUI, chatIconFile?: File, avatarFile?: File) {
        const web = this.webSource();
        if (!web) return;
        this.isLoading.set(true);
        this.httpS
            .saveLivechat$(
                web.id,
                // Both parts travel, so saving one never resets the other.
                {
                    enabled: !!this.settings()?.enabled,
                    preferences: LivechatPrefsUI.toBackendRequest(preferences),
                },
                chatIconFile,
                avatarFile
            )
            .pipe(finalize(() => this.isLoading.set(false)))
            .subscribe({
                next: settings => {
                    if (settings) this.patchLivechat(web.id, settings);
                    this.toastS.success('Preferences updated successfully');
                },
                error: error => {
                    this.toastS.error('Failed to update preferences');
                    console.error(error);
                },
            });
    }

    updateEnabled(update: boolean) {
        const web = this.webSource();
        if (!web) return;
        const previous = this.settings();
        if (previous) this.patchLivechat(web.id, { ...previous, enabled: update });
        this.httpS
            .saveLivechat$(web.id, {
                enabled: update,
                ...(previous?.preferences ? { preferences: previous.preferences } : {}),
            })
            .pipe(finalize(() => this.isLoading.set(false)))
            .subscribe({
                next: settings => {
                    if (settings) this.patchLivechat(web.id, settings);
                    this.toastS.success('Preferences updated successfully');
                },
                error: error => {
                    this.toastS.error('Failed to update preferences');
                    console.error(error);
                    this.patchLivechat(web.id, previous);
                },
            });
    }

    /** The AI agent that attends the website's LIVECHAT (and its WEBX); `null`: none. */
    setAgent(aiAgentId: string | null) {
        const web = this.webSource();
        if (!web) return;
        this.isSavingAgent.set(true);
        this.httpS
            .setAgent$(web.id, aiAgentId)
            .pipe(finalize(() => this.isSavingAgent.set(false)))
            .subscribe({
                // If the API ever answers with less than the website, apply the link locally.
                next: res =>
                    this.webSourcesS.upsert(res?.id ? res : { ...web, ai_agent_id: aiAgentId }),
                error: error => {
                    this.toastS.error('Failed to update preferences');
                    console.error(error);
                },
            });
    }

    private patchLivechat(id: string, settings: LivechatSettings | undefined) {
        this.webSourcesS.patch(id, { livechat_settings: settings });
    }
}
